import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Package, X, Image as ImageIcon, Video } from "lucide-react";

const formatPrice = (p) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(p);
const productCategories = [
  { value: "styling", label: "Styling" },
  { value: "perawatan", label: "Perawatan" },
  { value: "grooming", label: "Grooming" },
];

const MAX_IMAGES = 5;

const emptyForm = { name: "", description: "", price: "", stock: "", category: "perawatan", image_url: "", images: [], video_url: "", weight: "200" };

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);

  const fetchProducts = useCallback(async () => {
    try { const { data } = await api.get("/products/all"); setProducts(data); } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const openAdd = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    const imgs = Array.isArray(p.images) && p.images.length > 0 ? p.images : (p.image_url ? [p.image_url] : []);
    setForm({
      name: p.name,
      description: p.description || "",
      price: String(p.price),
      stock: String(p.stock),
      category: p.category,
      image_url: imgs[0] || "",
      images: imgs,
      video_url: p.video_url || "",
      weight: String(p.weight || 200),
    });
    setDialogOpen(true);
  };

  // Upload helper. Returns url.
  const upload = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url;
  };

  const handleImagesAdd = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const remaining = MAX_IMAGES - form.images.length;
    if (remaining <= 0) {
      toast.error(`Maksimal ${MAX_IMAGES} gambar per produk`);
      e.target.value = "";
      return;
    }
    const toUpload = files.slice(0, remaining);
    if (files.length > remaining) toast.info(`Hanya ${remaining} gambar pertama yang diupload (max ${MAX_IMAGES})`);
    setUploading(true);
    try {
      const urls = [];
      for (const f of toUpload) {
        const url = await upload(f);
        urls.push(url);
      }
      setForm(prev => {
        const merged = [...prev.images, ...urls].slice(0, MAX_IMAGES);
        return { ...prev, images: merged, image_url: prev.image_url || merged[0] || "" };
      });
      toast.success(`${urls.length} gambar terupload`);
    } catch { toast.error("Upload gagal"); } finally { setUploading(false); e.target.value = ""; }
  };

  const removeImage = (idx) => {
    setForm(prev => {
      const next = prev.images.filter((_, i) => i !== idx);
      return { ...prev, images: next, image_url: next[0] || "" };
    });
  };

  const handleVideoAdd = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Limit video file size to 30MB to avoid timeout
    if (file.size > 30 * 1024 * 1024) {
      toast.error("Ukuran video maksimal 30MB");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const url = await upload(file);
      setForm(prev => ({ ...prev, video_url: url }));
      toast.success("Video terupload");
    } catch { toast.error("Upload video gagal"); } finally { setUploading(false); e.target.value = ""; }
  };

  const removeVideo = () => setForm(prev => ({ ...prev, video_url: "" }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, price: parseInt(form.price), stock: parseInt(form.stock), weight: parseInt(form.weight) };
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Produk diperbarui");
      } else {
        await api.post("/products", payload);
        toast.success("Produk ditambahkan");
      }
      setDialogOpen(false);
      fetchProducts();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Hapus produk ini?")) return;
    try { await api.delete(`/products/${id}`); toast.success("Produk dihapus"); fetchProducts(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><div className="w-8 h-8 border-2 border-[#E60000] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-6 md:p-8" data-testid="products-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-white">Produk</h1>
          <p className="text-gray-400 text-sm mt-1">Kelola produk toko online</p>
        </div>
        <Button onClick={openAdd} className="bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase tracking-wider rounded-none h-10" data-testid="add-product-btn">
          <Plus size={16} className="mr-2" /> Tambah
        </Button>
      </div>

      <div className="bg-[#0F0F0F] border border-[#262626] overflow-x-auto" data-testid="products-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#262626]">
              <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Produk</th>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Kategori</th>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Media</th>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Harga</th>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Stok</th>
              <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Status</th>
              <th className="text-right px-5 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {products.map(p => {
              const imgCount = Array.isArray(p.images) ? p.images.length : (p.image_url ? 1 : 0);
              const hasVideo = !!p.video_url;
              return (
                <tr key={p.id} className="border-b border-[#262626]/50 hover:bg-[#1A1A1A] transition-colors" data-testid={`product-row-${p.id}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#1A1A1A] border border-[#262626] flex items-center justify-center shrink-0 overflow-hidden">
                        {p.image_url ? <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" /> : <Package size={14} className="text-gray-600" />}
                      </div>
                      <div><p className="text-white font-semibold">{p.name}</p><p className="text-gray-500 text-xs">{p.description?.substring(0, 40)}</p></div>
                    </div>
                  </td>
                  <td className="px-5 py-3"><Badge variant="secondary" className="uppercase text-[10px]">{p.category}</Badge></td>
                  <td className="px-5 py-3 text-xs text-gray-400 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1"><ImageIcon size={11} /> {imgCount}</span>
                    {hasVideo && <span className="ml-2 inline-flex items-center gap-1 text-[#E60000]"><Video size={11} /> 1</span>}
                  </td>
                  <td className="px-5 py-3 text-[#E60000] font-semibold">{formatPrice(p.price)}</td>
                  <td className="px-5 py-3 text-gray-400">{p.stock}</td>
                  <td className="px-5 py-3"><Badge className={p.is_active ? "bg-green-900/30 text-green-400" : "bg-red-900/30 text-red-400"}>{p.is_active ? "Aktif" : "Nonaktif"}</Badge></td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => openEdit(p)} className="p-1.5 text-gray-400 hover:text-white hover:bg-[#262626]" data-testid={`edit-product-${p.id}`}><Pencil size={14} /></button>
                      <button onClick={() => handleDelete(p.id)} className="p-1.5 text-gray-400 hover:text-[#E60000] hover:bg-[#262626]" data-testid={`delete-product-${p.id}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0F0F0F] border-[#262626] text-white max-w-xl rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase tracking-tight">{editing ? "Edit Produk" : "Tambah Produk"}</DialogTitle>
            <DialogDescription className="text-gray-400 text-sm">Isi detail produk toko online. Maks {MAX_IMAGES} gambar dan 1 video.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="product-form">
            <div>
              <Label className="text-gray-300 text-xs font-bold uppercase tracking-wider">Nama</Label>
              <Input className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="product-name-input" />
            </div>
            <div>
              <Label className="text-gray-300 text-xs font-bold uppercase tracking-wider">Deskripsi</Label>
              <Textarea
                className="mt-1 bg-[#1A1A1A] border-[#262626] text-white rounded-none min-h-[100px]"
                placeholder="Deskripsi produk (boleh panjang, line break didukung)..."
                value={form.description}
                onChange={e => setForm({...form, description: e.target.value})}
                data-testid="product-desc-input"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-gray-300 text-xs font-bold uppercase">Harga</Label><Input type="number" className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" value={form.price} onChange={e => setForm({...form, price: e.target.value})} required data-testid="product-price-input" /></div>
              <div><Label className="text-gray-300 text-xs font-bold uppercase">Stok</Label><Input type="number" className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" value={form.stock} onChange={e => setForm({...form, stock: e.target.value})} required data-testid="product-stock-input" /></div>
              <div><Label className="text-gray-300 text-xs font-bold uppercase">Berat (g)</Label><Input type="number" className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" value={form.weight} onChange={e => setForm({...form, weight: e.target.value})} data-testid="product-weight-input" /></div>
            </div>
            <div>
              <Label className="text-gray-300 text-xs font-bold uppercase tracking-wider">Kategori</Label>
              <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                <SelectTrigger className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" data-testid="product-category-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#262626]">{productCategories.map(c => <SelectItem key={c.value} value={c.value} className="text-white">{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {/* Images */}
            <div className="border border-[#262626] p-3 bg-[#0A0A0A]">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-gray-300 text-xs font-bold uppercase tracking-wider flex items-center gap-1"><ImageIcon size={12} /> Gambar ({form.images.length}/{MAX_IMAGES})</Label>
                <span className="text-[10px] text-gray-500">Gambar pertama jadi thumbnail</span>
              </div>
              {form.images.length > 0 && (
                <div className="grid grid-cols-5 gap-2 mb-2" data-testid="product-images-grid">
                  {form.images.map((url, idx) => (
                    <div key={idx} className="relative aspect-square bg-[#1A1A1A] border border-[#262626] group">
                      <img src={url} alt={`product ${idx + 1}`} className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removeImage(idx)} className="absolute top-1 right-1 w-5 h-5 bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-image-${idx}`}>
                        <X size={10} />
                      </button>
                      {idx === 0 && <span className="absolute bottom-1 left-1 text-[8px] bg-[#E60000] text-white px-1 py-0.5 font-bold uppercase">Cover</span>}
                    </div>
                  ))}
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImagesAdd}
                disabled={uploading || form.images.length >= MAX_IMAGES}
                className="w-full text-xs text-gray-400 file:mr-2 file:py-1.5 file:px-3 file:border file:border-[#262626] file:bg-[#1A1A1A] file:text-white file:text-[10px] file:font-bold file:uppercase disabled:opacity-50"
                data-testid="product-image-upload"
              />
            </div>

            {/* Video */}
            <div className="border border-[#262626] p-3 bg-[#0A0A0A]">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-gray-300 text-xs font-bold uppercase tracking-wider flex items-center gap-1"><Video size={12} /> Video (Max 1, otomatis tampil pertama)</Label>
              </div>
              {form.video_url ? (
                <div className="relative" data-testid="product-video-preview">
                  <video src={form.video_url} className="w-full max-h-48 bg-black" controls />
                  <button type="button" onClick={removeVideo} className="absolute top-1 right-1 w-6 h-6 bg-black/70 text-white flex items-center justify-center" data-testid="remove-video"><X size={12} /></button>
                </div>
              ) : (
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleVideoAdd}
                  disabled={uploading}
                  className="w-full text-xs text-gray-400 file:mr-2 file:py-1.5 file:px-3 file:border file:border-[#262626] file:bg-[#1A1A1A] file:text-white file:text-[10px] file:font-bold file:uppercase disabled:opacity-50"
                  data-testid="product-video-upload"
                />
              )}
              <p className="text-[10px] text-gray-500 mt-1">Max 30MB. Video otomatis di urutan pertama saat user lihat detail.</p>
            </div>

            {uploading && <p className="text-xs text-yellow-400">Mengupload...</p>}
            <Button type="submit" disabled={uploading} className="w-full bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase tracking-wider h-10 rounded-none disabled:opacity-50" data-testid="product-submit-btn">{editing ? "Simpan" : "Tambah"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
